import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Button, ButtonGroup, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, IconButton, InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelPresentationIcon from '@mui/icons-material/CancelPresentation';
import PrintIcon from '@mui/icons-material/Print';
import { DateTime } from 'luxon';
import RestAPIService from '../../Utility/Services/RestAPIService';
import { Serviceurls } from '../../Utility/API/Serviceurls';
import ToastMsg from '../../Components/ToastMsg/ToastMsg';
import Loader from '../../Components/Loader';
import { ImagePaths } from '../../Utility/ImagePaths';
import { Colors } from '../../Styles/Colors';

const SESSION_OPTIONS = ['Morning', 'Afternoon', 'Evening', 'Night'];

export default function MedicineTask() {
  const [patientList, setPatientList] = useState([]);
  const [medicineTaskList, setMedicineTaskList] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [sessionFilter, setSessionFilter] = useState('Morning');
  const [blockFilter, setBlockFilter] = useState('');
  const [floorFilter, setFloorFilter] = useState('');
  const [wardFilter, setWardFilter] = useState('');
  const [dateFilter, setDateFilter] = useState(DateTime.now().toFormat('yyyy-MM-dd'));
  const [selectedIds, setSelectedIds] = useState([]);
  const [remarksMap, setRemarksMap] = useState({});
  const [isLoader, setIsLoader] = useState(false);
  const [isErrorMsg, setIsErrorMsg] = useState(false);
  const [isErrorMsgText, setIsErrorMsgText] = useState('');
  const [successMsg, setSuccessMsg] = useState(false);
  const [successMsgText, setSuccessMsgText] = useState('');
  const [notTakenOpen, setNotTakenOpen] = useState(false);
  const [rowForNotTaken, setRowForNotTaken] = useState(null);
  const [notTakenRemarks, setNotTakenRemarks] = useState('');
  const [notTakenSubmitting, setNotTakenSubmitting] = useState(false);

  // For API we send prescription_detail_id (one per line). Selection tracks lines by prescription_detail_id.
  const allPendingLineIds = useMemo(() => {
    return (medicineTaskList || []).filter((line) => (line.drug_list || []).some((d) => d.status !== 'Taken')).map((line) => line.prescription_detail_id ?? line.id).filter(Boolean);
  }, [medicineTaskList]);
  const pendingLineCount = allPendingLineIds.length;

  const blocks = [...new Set(patientList.map((p) => p.block_name).filter(Boolean))];
  const floors = [...new Set(patientList.map((p) => p.floor_number).filter(Boolean))];
  const wards = [...new Set(patientList.map((p) => p.ward_name).filter(Boolean))];

  const fetchPatientList = useCallback(() => {
    setIsLoader(true);
    RestAPIService.getAll(`${Serviceurls.IP_TASK_PATIENT_LIST}?get_type=Vitals`)
      .then((res) => {
        if (res.data?.status === 'success') setPatientList(res.data?.data || []);
        else setPatientList([]);
        setIsLoader(false);
      })
      .catch(() => { setPatientList([]); setIsLoader(false); });
  }, []);

  const fetchMedicineTaskList = useCallback(() => {
    const params = new URLSearchParams();
    params.set('session', sessionFilter.toLowerCase());
    if (blockFilter) params.set('block', blockFilter);
    if (floorFilter) params.set('floor', floorFilter);
    if (wardFilter) params.set('ward', wardFilter);
    setIsLoader(true);
    RestAPIService.getAll(`${Serviceurls.IP_NURSE_MEDICINE_TASK}?${params.toString()}`)
      .then((res) => {
        if (res.data?.status !== 'success') {
          setMedicineTaskList([]);
          setIsLoader(false);
          return;
        }
        const raw = Array.isArray(res.data?.data) ? res.data.data : [];
        // Keep API structure: each item = one LINE (patient + instructions + comments + drug_list). Do NOT flatten.
        setMedicineTaskList(raw);
        setIsLoader(false);
      })
      .catch(() => { setMedicineTaskList([]); setIsLoader(false); });
  }, [sessionFilter, blockFilter, floorFilter, wardFilter]);

  useEffect(() => { fetchPatientList(); }, [fetchPatientList]);
  useEffect(() => { fetchMedicineTaskList(); }, [fetchMedicineTaskList]);
  useEffect(() => { setSelectedIds([]); }, [medicineTaskList]);

  const getSessionQuantity = (drug, line) => {
    const key = sessionFilter.toLowerCase();
    return (drug[`${key}_quantity`] ?? drug[`${key}`] ?? drug.quantity ?? line?.[`${key}_quantity`] ?? '-') + (drug?.uom ? ` ${drug?.uom}` : '');
  };

  const getLinePendingIds = (line) => (line.drug_list || []).filter((d) => d.status !== 'Taken').map((d) => d.id);
  const getLineDetailId = (line) => line.prescription_detail_id ?? line.id;
  const hasLinePending = (line) => (line.drug_list || []).some((d) => d.status !== 'Taken');

  const setRemark = (rowId, value) => setRemarksMap((prev) => ({ ...prev, [rowId]: value }));
  const toggleSelection = (prescriptionDetailId, checked) => setSelectedIds((prev) => (checked ? [...prev, prescriptionDetailId] : prev.filter((x) => x !== prescriptionDetailId)));
  const showError = (msg) => { setIsErrorMsg(true); setIsErrorMsgText(msg); };
  const showSuccess = (msg) => { setSuccessMsg(true); setSuccessMsgText(msg); };
  const msgClose = () => { setIsErrorMsg(false); setSuccessMsg(false); };

  const handleDoneSingle = (row, e) => {
    e?.stopPropagation?.();
    if (!row?.id) return;
    setIsLoader(true);
    const payload = { id: [row.id], session: sessionFilter.toLowerCase() };
    RestAPIService.create(payload, Serviceurls.IP_NURSE_MEDICINE_TASK)
      .then((res) => {
        setIsLoader(false);
        if (res.data?.status === 'success') { showSuccess(res.data?.message || 'Marked as Done.'); fetchMedicineTaskList(); }
        else showError(res.data?.message || 'Failed.');
      })
      .catch((err) => { setIsLoader(false); showError(err?.response?.data?.message || err?.message || 'Failed.'); });
  };

  const handleCompleteMultiple = () => {
    if (selectedIds.length === 0) { showError('Select at least one medicine task.'); return; }
    setIsLoader(true);
    const payload = { id: selectedIds, session: sessionFilter.toLowerCase() };
    RestAPIService.create(payload, Serviceurls.IP_NURSE_MEDICINE_TASK)
      .then((res) => {
        setIsLoader(false);
        setSelectedIds([]);
        if (res.data?.status === 'success') { showSuccess(res.data?.message || 'Selected tasks completed.'); fetchMedicineTaskList(); }
        else showError(res.data?.message || 'Failed.');
      })
      .catch((err) => { setIsLoader(false); showError(err?.response?.data?.message || err?.message || 'Failed.'); });
  };

  const openNotTaken = (row, e) => {
    e?.stopPropagation?.();
    if (!row?.id || row.status === 'Taken') return;
    setRowForNotTaken(row);
    setNotTakenRemarks(remarksMap[row.id] || '');
    setNotTakenOpen(true);
  };

  const closeNotTaken = () => { setNotTakenOpen(false); setRowForNotTaken(null); setNotTakenRemarks(''); };

  const handleCompleteForLine = (line, e) => {
    e?.stopPropagation?.();
    const detailId = getLineDetailId(line);
    if (!detailId || !hasLinePending(line)) return;
    setIsLoader(true);
    const payload = { id: [detailId], session: sessionFilter.toLowerCase() };
    RestAPIService.create(payload, Serviceurls.IP_NURSE_MEDICINE_TASK)
      .then((res) => {
        setIsLoader(false);
        setSelectedIds((prev) => prev.filter((id) => id !== detailId));
        if (res.data?.status === 'success') { showSuccess(res.data?.message || 'Marked as Done.'); fetchMedicineTaskList(); }
        else showError(res.data?.message || 'Failed.');
      })
      .catch((err) => { setIsLoader(false); showError(err?.response?.data?.message || err?.message || 'Failed.'); });
  };

  const openNotTakenForLine = (line, e) => {
    e?.stopPropagation?.();
    const detailId = getLineDetailId(line);
    if (!detailId || !hasLinePending(line)) return;
    setRowForNotTaken({ ids: [detailId], isGroup: true });
    setNotTakenRemarks('');
    setNotTakenOpen(true);
  };

  const handleNotTakenSubmit = () => {
    const ids = rowForNotTaken?.isGroup ? rowForNotTaken.ids : (rowForNotTaken?.id ? [rowForNotTaken.id] : []);
    if (ids.length === 0) return;
    setNotTakenSubmitting(true);
    const payload = {
      id: ids,
      get_type: 'Medicine Task',
      remark: notTakenRemarks.trim() || '',
      not_taken: true,
      session: sessionFilter.toLowerCase(),
    };
    RestAPIService.create(payload, Serviceurls.IP_PATIENT_NURSE_TASKS)
      .then((res) => {
        setNotTakenSubmitting(false);
        if (res.data?.status === 'success') {
          showSuccess(res.data?.message || 'Marked as Not Taken.');
          closeNotTaken();
          fetchMedicineTaskList();
        } else showError(res.data?.message || 'Failed.');
      })
      .catch((err) => { setNotTakenSubmitting(false); showError(err?.response?.data?.message || err?.message || 'Failed.'); });
  };
  const handlePrint = () => {
    try {
      const params = new URLSearchParams();
      // params.set('session', sessionFilter.toLowerCase());
      if (blockFilter) params.set('block', blockFilter);
      if (floorFilter) params.set('floor', floorFilter);
      if (wardFilter) params.set('ward', wardFilter);
      RestAPIService.getAll(Serviceurls.IP_NURSE_MEDICINE_TASK + `?${params.toString()}&export_type=pdf`)
        .then((res) => {
          const file = new Blob(
            [res.data],
            { type: 'application/pdf' });
          const fileURL = URL.createObjectURL(file);
          window.open(fileURL);
        })
        .catch((err) => { showError(err?.response?.data?.message || err?.message || 'Failed.'); });
    } catch (err) {
      showError(err?.response?.data?.message || err?.message || 'Failed.');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)', overflow: 'hidden', bgcolor: 'background.default' }}>
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Select Block</InputLabel>
                <Select value={blockFilter} label="Select Block" onChange={(e) => setBlockFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {blocks.map((b) => (<MenuItem key={b} value={b}>{b}</MenuItem>))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Select Floor</InputLabel>
                <Select value={floorFilter} label="Select Floor" onChange={(e) => setFloorFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {floors.map((f) => (<MenuItem key={f} value={f}>{f}</MenuItem>))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Select Ward</InputLabel>
                <Select value={wardFilter} label="Select Ward" onChange={(e) => setWardFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {wards.map((w) => (<MenuItem key={w} value={w}>{w}</MenuItem>))}
                </Select>
              </FormControl>
              <Button variant="contained" size="small" onClick={handleCompleteMultiple} disabled={selectedIds.length === 0} sx={{ textTransform: 'uppercase', bgcolor: Colors.Primary }}>
                Complete
              </Button>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField type="date" size="small" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
              <IconButton size="small" title="Print" onClick={handlePrint}><PrintIcon /></IconButton>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1.5, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <ButtonGroup size="small" disableElevation sx={{ '& .MuiButton-root': { textTransform: 'capitalize' } }}>
              {SESSION_OPTIONS.map((s) => (
                <Button key={s} variant={sessionFilter === s ? 'contained' : 'outlined'} onClick={() => setSessionFilter(s)} sx={{ minWidth: 90 }}>
                  {s}
                </Button>
              ))}
            </ButtonGroup>
          </Box>
          <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { borderRight: '1px solid', borderColor: 'divider' } }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ fontWeight: 600, bgcolor: 'grey.100', verticalAlign: 'top', borderRight: '1px solid', borderColor: 'divider' }}>
                  <Checkbox
                    indeterminate={selectedIds.length > 0 && selectedIds.length < pendingLineCount}
                    checked={pendingLineCount > 0 && selectedIds.length === pendingLineCount}
                    onChange={(e) => {
                      if (e.target.checked) setSelectedIds([...allPendingLineIds]);
                      else setSelectedIds([]);
                    }}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100', verticalAlign: 'top', borderRight: '1px solid', borderColor: 'divider' }}>Patient Details</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100', verticalAlign: 'top', borderRight: '1px solid', borderColor: 'divider' }}>Drug Name</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100', verticalAlign: 'top', borderRight: '1px solid', borderColor: 'divider' }}>{sessionFilter}</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100', verticalAlign: 'top', borderRight: '1px solid', borderColor: 'divider' }}>Available Quantity</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100', verticalAlign: 'top', borderRight: '1px solid', borderColor: 'divider' }}>Instruction</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100', verticalAlign: 'top', borderRight: '1px solid', borderColor: 'divider' }}>Comments</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, bgcolor: 'grey.100', verticalAlign: 'top', borderRight: 'none' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {medicineTaskList.flatMap((line, lineIndex) => {
                const drugs = line.drug_list || [];
                if (drugs.length === 0) return [];
                const patient = patientList.find((p) => p.ip_admission_id === line.ip_admission_id) || {};
                const instruction = line.instructions ?? '-';
                const instructionDisplay = instruction !== '-' && instruction.length > 25 ? (
                  <Tooltip title={instruction}><span>{instruction.slice(0, 25) + '...'}</span></Tooltip>
                ) : instruction;
                const comments = line.comments ?? '-';
                const commentsDisplay = comments !== '-' && comments.length > 25 ? (
                  <Tooltip title={comments}><span>{comments.slice(0, 25) + '...'}</span></Tooltip>
                ) : comments;
                const detailId = getLineDetailId(line);
                const linePending = hasLinePending(line);
                const checked = selectedIds.includes(detailId);
                const rowSpan = drugs.length;
                const borderColor = lineIndex % 2 === 0 ? 'gray' : '#4da3d4';
                const tableCellSx = { verticalAlign: 'top', borderRight: '1px solid', borderColor: 'divider' };
                return drugs.map((drug, idx) => {
                  const isFirstRow = idx === 0;
                  return (
                    <TableRow key={`${line.prescription_detail_id ?? lineIndex}-${drug.id}`} hover sx={{ borderLeft: `5px solid ${borderColor}` }}>
                      {isFirstRow ? (
                        <TableCell rowSpan={rowSpan} padding="checkbox" sx={tableCellSx}>
                          <Checkbox
                            checked={checked}
                            disabled={!linePending}
                            onChange={(e) => toggleSelection(detailId, e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                      ) : null}
                      {isFirstRow && (
                        <TableCell rowSpan={rowSpan} sx={tableCellSx}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box component="img" src={patient.photo_url || line.photo_url || (line.patient_gender === 'm' || line.patient_gender === 'Male' ? ImagePaths.Male?.default : ImagePaths.Female?.default)} alt="" sx={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                            <Box>
                              <Typography variant="body2">{line.patient_name || patient.patient_name || '-'} {line.patient_age || patient.patient_age}</Typography>
                              <Typography variant="caption" color="text.secondary">{line.patient_account_number || patient.patient_account_number || '-'} | {line.mobile_number || patient.mobile_number || '-'}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                      )}
                      <TableCell sx={tableCellSx}>
                        <Typography variant="body2">{drug.drug_name || '-'}</Typography>
                        {drug.dosage_type && <Typography variant="caption" color="text.secondary">{drug.dosage_type}</Typography>}
                      </TableCell>
                      <TableCell sx={tableCellSx}>{getSessionQuantity(drug, line)}</TableCell>
                      <TableCell sx={tableCellSx}>{drug.available_quantity ?? '-'}</TableCell>
                      {isFirstRow && (
                        <TableCell rowSpan={rowSpan} sx={tableCellSx}>{instructionDisplay}</TableCell>
                      )}
                      {isFirstRow && (
                        <TableCell rowSpan={rowSpan} sx={tableCellSx}>{commentsDisplay}</TableCell>
                      )}
                      {isFirstRow && (
                        <TableCell rowSpan={rowSpan} align="center" sx={{ ...tableCellSx, borderRight: 'none' }}>
                        {linePending && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              <IconButton size="small" color="success" onClick={(e) => handleCompleteForLine(line, e)} title="Approve / Done">
                                <CheckCircleOutlineIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" color="warning" onClick={(e) => openNotTakenForLine(line, e)} title="Not Taken">
                                <CancelPresentationIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                });
              })}
            </TableBody>
          </Table>
          {medicineTaskList.length === 0 && !isLoader && (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No medicine tasks</Typography>
          )}
        </Paper>
      </Box>

      <Dialog open={notTakenOpen} onClose={closeNotTaken} maxWidth="sm" fullWidth>
        <DialogTitle>Not Taken</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Add remarks (optional) for marking this task as Not Taken.</Typography>
          <TextField fullWidth size="small" multiline rows={3} placeholder="Remarks (optional)" value={notTakenRemarks} onChange={(e) => setNotTakenRemarks(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={closeNotTaken} sx={{ textTransform: 'capitalize' }}>Cancel</Button>
          <Button variant="contained" onClick={handleNotTakenSubmit} disabled={notTakenSubmitting} sx={{ textTransform: 'capitalize' }}>{notTakenSubmitting ? 'Submitting...' : 'Submit'}</Button>
        </DialogActions>
      </Dialog>

      {isErrorMsg && <ToastMsg severity="error" msg={isErrorMsgText} msgPop={msgClose} />}
      {successMsg && <ToastMsg severity="success" msg={successMsgText} msgPop={msgClose} />}
      <Loader loaderOpen={isLoader} />
    </Box>
  );
}
